import React from 'react';
import SeoMeta from '../components/SeoMeta';
import ArticleLayout from '../components/articles/ArticleLayout';
import { Callout, Points, Checks, ArticleLink, Figure } from '../components/articles/blocks';
import { FaqList, faqSchema } from '../components/common/B2B';

/*
 * "¿Qué es el DeCA?" — pieza informacional del eje 4 de la estrategia SEO
 * (ago-2026). Ataca la familia de consultas "que es el deca" (235 impresiones,
 * CTR 0,4 %, posición 9,4 en GSC a 19-ago-2026), que la página de producto
 * /documentos/documento-control no captura bien porque su intención es
 * transaccional. Todos los datos normativos proceden de páginas ya publicadas
 * y verificadas (DocumentoControlPage, FormatoPDFA1APage): no añadir
 * afirmaciones normativas nuevas sin contrastarlas.
 *
 * 4-nov-2026: fuera Control DeCA y la desambiguación con la DECA académica
 * (Jesús). «Un ejemplo de DeCA» con el DeCA multientrega pintado por el
 * renderizador real con datos ficticios (el mismo de /documentos/documento-control),
 * porque el artículo recibe búsquedas de «ejemplo deca» / «deca ejemplo» (GSC).
 */

const CANONICAL = 'https://www.bluecmr.com/articulos/que-es-el-deca';

const FAQS = [
  {
    q: '¿Qué es el DeCA en transporte?',
    a: 'En el transporte de mercancías por carretera, el DeCA es el Documento electrónico de Control Administrativo: el documento de control obligatorio que acompaña a cada envío y que desde el 5 de octubre de 2026 debe emitirse en digital.'
  },
  {
    q: '¿Qué significa DeCA?',
    a: 'DeCA son las siglas de Documento electrónico de Control Administrativo. Es la denominación oficial que la Resolución de 5 de junio de 2026 da al documento de control del transporte de mercancías por carretera cuando se emite en formato electrónico.'
  },
  {
    q: '¿El DeCA es un documento nuevo?',
    a: 'No. Es el documento de control de siempre (Orden FOM/2861/2012), antes conocido también como DCA. Lo que cambia es el formato: desde el 5 de octubre de 2026 debe emitirse obligatoriamente en electrónico, y con ese cambio llega el nombre nuevo.'
  },
  {
    q: '¿Quién está obligado a emitir el DeCA?',
    a: 'La responsabilidad es compartida: el cargador lo formaliza y el transportista lo formaliza y lo lleva a bordo. Ambos responden de que exista y esté bien cumplimentado (arts. 4 y 7 de la Orden FOM/2861/2012).'
  },
  {
    q: '¿Hace falta firmar el DeCA?',
    a: 'No. La firma no es requisito de validez del DeCA. Si se firma —por ejemplo, cuando el documento se usa además con finalidad contractual—, debe ser firma electrónica avanzada (AdES, art. 26 de eIDAS) o cualificada (QES).'
  },
  {
    q: '¿Puede la carta de porte sustituir al DeCA?',
    a: 'Sí, en transporte nacional, siempre que la carta de porte sea digital, cumpla los requisitos técnicos y contenga todos los datos obligatorios del DeCA. Así, un único documento cubre la función contractual y la administrativa.'
  },
  {
    q: '¿Qué pasa si no llevo el DeCA desde el 5 de octubre de 2026?',
    a: 'La Disposición Transitoria 8ª de la Ley 9/2025 no prevé periodo transitorio sin sanción: desde el 5 de octubre de 2026 el incumplimiento se sanciona conforme al régimen sancionador de la LOTT (arts. 141 y 143).'
  },
];

const REQUISITOS = [
  <>Un <strong>PDF nativo digital</strong> (no un escaneo ni una foto), con un tamaño máximo de <strong>5 MB</strong> y metadatos de fecha y hora de creación y modificación.</>,
  <>Un <strong>código QR</strong> que enlace a una <strong>URL HTTPS de descarga directa</strong> del documento, sin autenticación ni pasos intermedios: la inspección escanea y ve el PDF al instante.</>,
  <>Disponible durante todo el transporte y <strong>conservado al menos 1 año</strong>.</>,
  <>Emitido <strong>antes de iniciar el transporte</strong>, con los datos del cargador, el transportista, el vehículo, el origen, el destino y la mercancía.</>,
];

const QueEsElDecaPage = () => {
  return (
    <>
      <SeoMeta
        title="Qué es el DeCA de transporte: el documento de control digital | BlueCMR"
        description="Qué es el DeCA (Documento electrónico de Control Administrativo), a quién obliga, qué requisitos técnicos exige y cómo cumplirlo, con un ejemplo."
        canonical={CANONICAL}
        type="article"
        keywords="que es el deca transporte, deca transporte, documento deca transporte, que es el deca, deca, documento electrónico de control administrativo, deca transporte, que es el deca en transporte, deca que es, documento deca, normativa deca, deca obligatorio, documento de control, deca digital"
        breadcrumbs={[
          { name: 'Inicio', url: 'https://bluecmr.com/' },
          { name: 'Artículos', url: 'https://bluecmr.com/articulos' },
          { name: '¿Qué es el DeCA de transporte?', url: CANONICAL },
        ]}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: '¿Qué es el DeCA de transporte? El documento de control digital, explicado',
            description: 'Qué es el DeCA (Documento electrónico de Control Administrativo), a quién obliga desde el 5 de octubre de 2026 y qué requisitos técnicos exige.',
            image: 'https://www.bluecmr.com/og/articulo-que-es-el-deca.jpg',
            datePublished: '2026-08-19',
            dateModified: '2026-11-04',
            author: { '@type': 'Organization', name: 'BlueCMR', url: 'https://bluecmr.com' },
            publisher: { '@type': 'Organization', name: 'BlueCMR', logo: { '@type': 'ImageObject', url: 'https://www.bluecmr.com/og/logo-bluecmr.png' } },
            mainEntityOfPage: { '@type': 'WebPage', '@id': CANONICAL },
            articleSection: 'Normativa',
            keywords: 'que es el deca, documento electrónico de control administrativo, deca transporte, normativa deca',
          },
          faqSchema(FAQS),
        ]}
      />
      <ArticleLayout
        url="/articulos/que-es-el-deca"
        title="¿Qué es el DeCA de transporte?"
        deck="El nombre nuevo del documento de control del transporte, que desde el 5 de octubre de 2026 solo vale en digital. Aquí lo tiene explicado sin jerga."
        updated="2026-11-04"
        readingMinutes={6}
        toc={[
          { id: 'nombre-nuevo', label: 'Un nombre nuevo' },
          { id: 'a-quien-obliga', label: '¿A quién obliga y desde cuándo?' },
          { id: 'requisitos', label: 'Requisitos de un DeCA válido' },
          { id: 'ejemplo', label: 'Un ejemplo de DeCA' },
          { id: 'firma', label: '¿Hay que firmarlo?' },
          { id: 'deca-carta-porte-ecmr', label: 'DeCA, carta de porte y eCMR' },
          { id: 'sanciones', label: '¿Qué pasa si no lo lleva?' },
          { id: 'como-emitir', label: 'Cómo emitir su DeCA' },
          { id: 'preguntas-frecuentes', label: 'Preguntas frecuentes' },
        ]}
        closing={{ title: '¿Cumple ya con el DeCA digital?', text: 'Cuéntenos su operativa y le decimos cuál es el camino más rápido para cumplir.' }}
      >
        <Callout>
          <p>
            El <strong>DeCA</strong> (<strong>Documento electrónico de Control Administrativo</strong>) es el
            documento que debe acompañar a toda mercancía transportada por carretera en territorio nacional
            para que la inspección pueda comprobar los datos del envío. Es el <strong>documento de
            control</strong> de siempre (Orden FOM/2861/2012), con una novedad decisiva: desde el{' '}
            <strong>5 de octubre de 2026</strong> tiene que emitirse obligatoriamente en formato electrónico.
          </p>
        </Callout>

        <h2 id="nombre-nuevo">Un nombre nuevo para un documento que ya conocía</h2>
        <p>
          Si lleva años en el transporte, este documento lo ha llamado <strong>documento de control</strong>{' '}
          o <strong>DCA</strong>. La <strong>Resolución de 5 de junio de 2026</strong> de la Dirección General
          de Transporte por Carretera y Ferrocarril, al definir los requisitos técnicos de la versión
          electrónica, consolidó la denominación oficial: <strong>DeCA</strong>.
        </p>
        <p>
          El fondo no cambia: sigue siendo el justificante administrativo que regula la Orden FOM/2861/2012
          y que ampara cada transporte público de mercancías por carretera. Lo que cambia es el formato — del
          papel (o el PDF improvisado) a un <strong>documento electrónico con requisitos técnicos concretos</strong> —
          y, con él, el nombre con el que lo verá en la normativa, en las inspecciones y en las búsquedas.
        </p>
        <Callout title="Equivalencia rápida">
          <p>
            DeCA = documento de control = DCA. Tres nombres, un mismo documento. La versión en papel dejó de
            ser válida para el transporte interior el 5 de octubre de 2026.
          </p>
        </Callout>

        <h2 id="a-quien-obliga">¿A quién obliga y desde cuándo?</h2>
        <p>
          La <strong>Disposición Transitoria 8ª de la Ley 9/2025 de Movilidad Sostenible</strong> fija la
          fecha: <strong>5 de octubre de 2026</strong>. Desde ese día, el transporte interior —nacional y
          cabotaje— ya no admite papel (art. 222 del ROTT), y no hay periodo transitorio sin sanción.
        </p>
        <Points
          items={[
            {
              title: 'Responsabilidad compartida',
              text: <>El <strong>cargador</strong> formaliza el DeCA y el <strong>transportista</strong> lo formaliza y lo lleva a bordo. Ambos responden de que exista y esté bien cumplimentado (arts. 4 y 7 de la Orden FOM/2861/2012). Si subcontrata un transporte, pasa a ser cargador contractual del nuevo envío.</>,
            },
            {
              title: 'Quién queda fuera',
              text: <>Están exentos el <strong>transporte privado complementario</strong> (que debe acreditar el art. 102.2 de la LOTT) y la <strong>paquetería de bultos reducidos</strong>. El transporte <strong>internacional</strong> no usa el DeCA: se documenta con los convenios internacionales (CMR) suscritos por España.</>,
            },
          ]}
        />

        <h2 id="requisitos">Qué requisitos debe cumplir un DeCA válido</h2>
        <p>
          La Resolución de 5 de junio de 2026 define cómo tiene que ser el fichero. No basta con "tenerlo en
          el móvil": el documento debe cumplir requisitos técnicos comprobables en una inspección.
        </p>
        <Checks items={REQUISITOS} />
        <p>
          Como norma general, <strong>cada envío necesita su propio DeCA</strong>. La Resolución permite{' '}
          <strong>agrupar varios envíos en un único DeCA</strong> cuando comparten el mismo cargador
          contractual y el mismo transportista efectivo, con cada envío identificado individualmente.
        </p>
        <ArticleLink
          to="/articulos/formato-pdfa1a-documento-control-digital-2026"
          title="Requisitos técnicos del DeCA según la Resolución de junio de 2026"
          text="¿Quiere el detalle técnico completo?"
        />

        <h2 id="ejemplo">Un ejemplo de DeCA</h2>
        <p>
          Así se ve un DeCA electrónico emitido con BlueCMR: el cargador, el transportista, el vehículo, la
          mercancía y, en este caso, varias entregas agrupadas en un solo documento, con el código QR que abre
          el PDF en una inspección.
        </p>
        <Figure
          src="/images/documentos/deca-clasico-completo.webp"
          alt="Ejemplo de DeCA (documento electrónico de control administrativo) con varias entregas agrupadas, emitido con BlueCMR"
          width="1100"
          height="1081"
          caption="Ejemplo de DeCA con tres entregas agrupadas. Datos ficticios."
        />

        <h2 id="firma">¿Hay que firmarlo?</h2>
        <p>
          <strong>No: la firma no es requisito de validez del DeCA.</strong> Ahora bien, si el documento se
          firma —por ejemplo, porque se usa también con finalidad contractual, como una carta de porte—,
          la firma debe ser <strong>electrónica avanzada (AdES, art. 26 de eIDAS)</strong> o{' '}
          <strong>cualificada (QES)</strong>. Una firma dibujada sin más sobre un PDF no cumple.
        </p>

        <h2 id="deca-carta-porte-ecmr">DeCA, carta de porte y eCMR: cuál es cuál</h2>
        <p>
          El <strong>DeCA</strong> es un documento <strong>administrativo</strong>: existe para que la
          inspección compruebe el envío. La <strong>carta de porte</strong> es un documento{' '}
          <strong>contractual</strong> entre cargador y transportista, y el <strong>CMR/eCMR</strong> es su
          equivalente para el transporte <strong>internacional</strong>. En transporte nacional, una carta de
          porte digital que cumpla los requisitos técnicos y contenga todos los datos obligatorios del DeCA{' '}
          <strong>puede sustituirlo</strong>: un solo documento para las dos funciones.
        </p>
        <ArticleLink
          to="/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr"
          title="Comparativa completa: DeCA vs Carta de Porte vs CMR/eCMR"
        />

        <h2 id="sanciones">¿Qué pasa si no lo lleva?</h2>
        <Callout>
          <p>
            Desde el 5 de octubre de 2026 <strong>no hay periodo de gracia</strong>: la Ley 9/2025 no prevé
            un transitorio sin sanción, y el incumplimiento se sanciona conforme al régimen sancionador de la{' '}
            <strong>LOTT (arts. 141 y 143)</strong>. Llevarlo en papel, en un escaneo o en un PDF que no cumpla
            los requisitos técnicos equivale a no llevarlo bien.
          </p>
        </Callout>

        <h2 id="como-emitir">Cómo emitir su DeCA con BlueCMR</h2>
        <p>
          BlueCMR emite el DeCA con los requisitos de la Resolución de 5 de junio de 2026 y lo hace llegar al
          conductor en su móvil, sin instalar nada. Y si lo necesita, en la misma plataforma: carta de porte y
          eCMR, firmas, eventos en ruta, integración con su TMS o ERP y conservación con evidencias.
        </p>
        <ArticleLink
          to="/documentos/documento-control"
          title="El DeCA en BlueCMR"
          text="Cómo se emite, qué ve el conductor y qué queda de cada transporte."
        />

        <h2 id="preguntas-frecuentes">Preguntas frecuentes sobre el DeCA</h2>
        <FaqList items={FAQS} />
      </ArticleLayout>
    </>
  );
};

export default QueEsElDecaPage;
